import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { List } from '~/Components/Icons';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { useDateFns, useNavigation, useProvider } from '~/Hooks';
import { CalendarView } from '~/Models/DTO/Calendar.model';
import { Button, IconButton, MenuItem, Select, Typography } from '~/UIKit';
import { useStyles } from './CalendarHeader.styles';
import { SegmentName } from '~/Constants/Segment.constants';
import { Segment } from '~/Helpers/Segment.helper';

export interface CalendarHeaderProps {
  toggleList: () => void;
  listOpen: boolean;
  view: CalendarView;
  onTodayClick: () => void;
  navigateDate: (forward: boolean) => void;
  selectedDay: Date;
  setCalendarView: (e: SelectChangeEvent<CalendarView>) => void;
  filterComponent?: React.ReactNode;
}

export const CalendarHeader = ({
  toggleList,
  listOpen,
  view,
  onTodayClick,
  navigateDate,
  selectedDay,
  setCalendarView,
  filterComponent,
}: CalendarHeaderProps) => {
  const { classes } = useStyles();
  const { navigate } = useNavigation();
  const { currentProvider } = useProvider();
  const { t } = useTranslation();
  const { formatDate } = useDateFns();

  const weekday = selectedDay.toLocaleString('default', { weekday: 'short' });
  const domain = currentProvider?.domain || '';
  const handleCreateEvent = () => {
    Segment.track(SegmentName.CREATE_EVENT_FROM_CALENDAR_PAGE);
    navigate(routeReplacer(Route.EVENT_CREATE, { domain }));
  };
  const getViewOptions = () => [
    { value: CalendarView.DAY, label: 'app.calendar.day' },
    { value: CalendarView.WEEK, label: 'app.calendar.week' },
  ];
  const calendarViewOptions = getViewOptions();

  return (
    <div className={classes.headerContainer}>
      <div className={classes.headerButtonsLeft}>
        <Button onClick={toggleList} color="primary" size="small" data-testid="calendar-listToggle">
          <List className={classes.icon} />{' '}
          {listOpen ? t('app.calendar.hideList') : t('app.calendar.showList')}
        </Button>
        <Button
          onClick={onTodayClick}
          variant="outlined"
          color="primary"
          size="small"
          data-testid="calendar-today"
        >
          {t('app.today')}
        </Button>
        <div className={classes.datePeriodSelector}>
          <IconButton className={classes.arrow} onClick={() => navigateDate(false)} size="small">
            <KeyboardArrowLeft />
          </IconButton>
          <IconButton className={classes.arrow} onClick={() => navigateDate(true)} size="small">
            <KeyboardArrowRight />
          </IconButton>
          <Typography className={classes.selectedDay}>
            {weekday} {formatDate(selectedDay.toISOString(), 'dd LLL')}
          </Typography>
        </div>
      </div>
      <div className={classes.headerButtonsRight}>
        <Select
          newDesign
          classes={{ select: classes.select, icon: classes.selectIcon }}
          className={classes.selectRoot}
          data-testid="CalenderViewSelect"
          defaultValue={view}
          onChange={setCalendarView}
        >
          {calendarViewOptions.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
        <Button
          onClick={handleCreateEvent}
          variant="contained"
          color="primary"
          size="small"
          data-testid="calendar-newEvent"
        >
          {`+ ${t('event.createNew')}`}
        </Button>
        {filterComponent}
      </div>
    </div>
  );
};
